// Builds request payloads for the Slot Availability Calendar API from the UI
// input types in interfaces/SlotCalendarObject.ts.
//
// Those input types were designed to already match the wire shape (see that
// file's header comment), so most functions here are near pass-through — the
// point of routing every write through this file anyway is the same reason
// AI_BULK_IMAGE_ANALYZE and every other multi-field payload in this repo goes
// through a Convertor: ONE place changes if the backend ever renames a field,
// instead of every call site.

import {
  CreateAvailabilitySlotInput,
  CreateSlotBlockInput,
  CreateSlotBookingEntryInput,
  CreateSlotScheduleVersionInput,
  PreviewSlotCalendarInput,
  PreviewSlotScheduleInput,
  SlotCalendarWriteInput,
  SlotScheduleRuleInput,
  UpdateAvailabilitySlotInput,
  UpdateSlotBookingEntryInput,
} from "../../../interfaces/SlotCalendarObject";

function buildRulePayload(rule: SlotScheduleRuleInput) {
  return {
    // A brand-new rule authored in the UI has no ruleId yet — mint one here
    // rather than asking every caller to remember to. An edited existing
    // rule already carries its ruleId through untouched.
    ruleId: rule.ruleId ?? crypto.randomUUID(),
    anchorDate: rule.anchorDate,
    untilDate: rule.untilDate,
    repeatEvery: rule.repeatEvery,
    repeatUnit: rule.repeatUnit,
    weekdays: rule.weekdays,
    localStartTime: rule.localStartTime,
    localEndTime: rule.localEndTime,
  };
}

function buildScheduleVersionPayload(input: CreateSlotScheduleVersionInput) {
  return {
    // Omitted entirely (not sent as null) when absent, so the server's own
    // "first version may omit effectiveFrom" rule applies rather than the
    // server seeing an explicit null and rejecting it.
    ...(input.effectiveFrom ? { effectiveFrom: input.effectiveFrom } : {}),
    availabilityUntilExclusive: input.availabilityUntilExclusive,
    kind: input.kind,
    durationValue: input.durationValue,
    durationUnit: input.durationUnit,
    defaultCapacity: input.defaultCapacity,
    capacityLabelSingular: input.capacityLabelSingular,
    capacityLabelPlural: input.capacityLabelPlural,
    bookingCutoff: input.bookingCutoff,
    defaultPublicLabel: input.defaultPublicLabel,
    // Backend rejects null here - always an object.
    defaultProperties: input.defaultProperties ?? {},
    rules: input.rules.map(buildRulePayload),
  };
}

/** PUT B body. */
function buildSlotCalendarPayload(input: SlotCalendarWriteInput) {
  return {
    timezone: input.timezone,
    published: input.published,
  };
}

/** POST B/schedule-versions body. */
function buildCreateScheduleVersionPayload(input: CreateSlotScheduleVersionInput) {
  return buildScheduleVersionPayload(input);
}

/** POST /slot-calendar-previews body. */
function buildPreviewCalendarPayload(input: PreviewSlotCalendarInput) {
  return {
    timezone: input.timezone,
    schedule: buildScheduleVersionPayload(input.schedule),
    fromDate: input.fromDate,
    toDateExclusive: input.toDateExclusive,
  };
}

/** POST B/schedule-previews body. */
function buildPreviewSchedulePayload(input: PreviewSlotScheduleInput) {
  return {
    schedule: buildScheduleVersionPayload(input.schedule),
    fromDate: input.fromDate,
    toDateExclusive: input.toDateExclusive,
  };
}

/** POST B/slots body. */
function buildCreateAvailabilitySlotPayload(input: CreateAvailabilitySlotInput) {
  return {
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    kind: input.kind,
    bookingCutoff: input.bookingCutoff,
    capacity: input.capacity,
    capacityLabelSingular: input.capacityLabelSingular,
    capacityLabelPlural: input.capacityLabelPlural,
    operationalStatus: input.operationalStatus,
    publicLabel: input.publicLabel,
    publicProperties: input.publicProperties ?? {},
    privateNote: input.privateNote,
  };
}

/** PUT B/slots/{slotId} body — full replacement of the writable fields only
 *  (times/source/labels/cutoff/kind are immutable, so they're never sent). */
function buildUpdateAvailabilitySlotPayload(input: UpdateAvailabilitySlotInput) {
  return {
    ...(input.occurrence ? { occurrence: input.occurrence } : {}),
    capacity: input.capacity,
    statusOverride: input.statusOverride,
    reapplyStatusOverride: input.reapplyStatusOverride,
    publicLabel: input.publicLabel,
    publicProperties: input.publicProperties ?? {},
    privateNote: input.privateNote,
    archived: input.archived,
  };
}

/** POST B/slots/{slotId}/booking-entries body. */
function buildCreateBookingEntryPayload(input: CreateSlotBookingEntryInput) {
  return {
    entryId: input.entryId,
    occurrence: input.occurrence,
    quantity: input.quantity,
    nameOrReference: input.nameOrReference,
    privateNote: input.privateNote,
  };
}

/** PUT B/slots/{slotId}/booking-entries/{entryId} body. */
function buildUpdateBookingEntryPayload(input: UpdateSlotBookingEntryInput) {
  return {
    quantity: input.quantity,
    nameOrReference: input.nameOrReference,
    privateNote: input.privateNote,
    status: input.status,
  };
}

/** POST B/blocks (and B/block-previews, same shape) body. */
function buildCreateBlockPayload(input: CreateSlotBlockInput) {
  return {
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    operationalStatus: input.operationalStatus,
    privateReason: input.privateReason,
    allowBookedSlots: input.allowBookedSlots,
  };
}

const SlotCalendarConvertor = {
  buildSlotCalendarPayload,
  buildCreateScheduleVersionPayload,
  buildPreviewCalendarPayload,
  buildPreviewSchedulePayload,
  buildCreateAvailabilitySlotPayload,
  buildUpdateAvailabilitySlotPayload,
  buildCreateBookingEntryPayload,
  buildUpdateBookingEntryPayload,
  buildCreateBlockPayload,
};

export default SlotCalendarConvertor;
